import axios from "axios";
import dayjs from "dayjs";
import OperationTypes from "../../../types";

export enum EnumStatusReasons {
  AVAILABLE = "AVAILABLE",
  NOT_AVAILABLE = "NOT_AVAILABLE",
}

export enum EnumReasonCodeState {
  OPEN = "OPEN",
  POTENTIAL = "POTENTIAL",
  CLOSED = "CLOSED",
}

export interface IUnavailabilityReasonRequest {
  id: number | null;
  reasonCodeState?: string;
  statusReasonCode: string;
  expectedStartUnavailabilityDatetime: string;
  actualStartUnavailabilityDatetime: string | null;
  expectedEndUnavailabilityDatetime?: string | null;
  actualEndUnavailabilityDatetime?: string | null;
  notes?: string;
}

const VehicleAvailabilityService = {
  get: {
    allDrivers: (tenant: string) => {
      return axios
        .get(`/masterdatatransportation/driver/all`, {
          params: { tenant: tenant },
        })
        .then(({ data }) => {
          return data.map((driver: any) => {
            return {
              ...driver,
              label: `${driver.name} ${driver.surname}`,
              value: driver.sapDriverId,
            };
          });
        });
    },
    vehicleByPlate: (tenant: string, plate: string) => {
      return axios
        .get<OperationTypes.Vehicle>(
          `masterdatavehicle/vehicle/plate/${plate}`,
          { params: { tenant: tenant } }
        )
        .then((response) => response.data);
    },

    downloadDeliveryNote: (tenant: string, id: number | null) => {
      return axios
        .get<OperationTypes.Vehicle>(
          `/masterdatatransportation/deliveryNote/${id}/file`,
          {
            params: { tenant: tenant },
            responseType: "blob",
          }
        )
        .then((response) => response);
    },

    availabilityStatusReasons: (
      tenant: string,
      statusReason: EnumStatusReasons
    ) => {
      return axios
        .get<OperationTypes.Domain[]>("masterdatavehicle/statusreason/all", {
          params: {
            tenant: tenant,
            valid: true,
            status: statusReason,
            includeSpecialCodes: false,
          },
        })
        .then((response) => response.data);
    },
    unavailabilityByVehicle: (tenant: string, id: number) => {
      return axios
        .get<OperationTypes.VehicleAvailability.Status>(
          `masterdatavehicle/unavailability/unavailabilityByVehicle/${id}`,
          { params: { tenant: tenant } }
        )
        .then((response) => response.data);
    },

    destinationLocations: (tenant: string) => {
      return axios
        .get(`/masterdatasupplier/location/locationsByFilters`, {
          params: {
            tenant: tenant,
            pageNumber: 0,
            pageSize: 1000000,
            currentStatus: "CREATED"
          },
        })
        .then(({ data }) => {
          return data.content.map((location: any) => {
            return {
              ...location,
              label: location.locationEntityDesc,
              value: location.locationEntityDesc,
            };
          });
        });
    },

    destinationLocationsFrom: (tenant: string) => {
      return axios
        .get(`/masterdatasupplier/location/locationsByFilters`, {
          params: {
            tenant: tenant,
            pageNumber: 0,
            pageSize: 1000000
          },
        })
        .then(({ data }) => {
          return data.content.map((location: any) => {
            return {
              ...location,
              label: location.locationEntityDesc,
              value: location.locationEntityDesc,
            };
          });
        });
    },

    locationDistance: (
      tenant: string,
      startLocationBpCode: string,
      endLocationBpCode: string
    ) => {
      return axios
        .get(`/masterdatasupplier/location/distanceByGoogle`, {
          params: {
            tenant: tenant,
            startLocationBpCode: startLocationBpCode,
            endLocationBpCode: endLocationBpCode,
          },
        })
        .then(({ data }) => {
          return data;
        });
    },

    allLocations: (tenant: string) => {
      return axios
        .get(`/masterdatavehicle/location/all`, { params: { tenant: tenant } })
        .then(({ data }) => {
          return data.map((location: any) => {
            return {
              ...location,
              label: location.description,
              value: location.description,
            };
          });
        });
    },

    damageForm: (tenant: string, plate: string) => {
      return axios
        .get(`/masterdatadamage/damageform/withdamages/byplate`, {
          params: {
            tenant: tenant,
            plate: plate,
          },
        })
        .then(({ data }) => {
          return data?.map((damage: any) => {
            return {
              ...damage,
              label: damage.code,
              value: damage.id,
            };
          });
        });
    },
  },

  add: {
    confirmExit: (tenant: string, data: any) => {
      return axios
        .post("masterdatatransportation/operatorapp/checkout", data, {
          params: { tenant: tenant },
        })
        .then((response) => response.data);
    },
  },
};

export default VehicleAvailabilityService;
